import React from 'react';
import { AppStoreAppCard } from '@/components/ui/app-store-app-card';
import { ResponsiveGrid } from '@/components/ui/responsive-grid';
import { cn } from '@/lib/utils';
import { WindowBreakpoints } from '@/hooks/useWindowResponsive';

interface App {
  id: string;
  name: string;
  description?: string;
  category: string;
  icon_name: string;
  component_type: string;
}

interface AppStoreSectionProps {
  title: string;
  subtitle?: string;
  apps: App[];
  onAppAction: (action: string, appId: string, appName: string) => void;
  appStore: any;
  variant?: 'grid' | 'list';
  className?: string;
  showEmpty?: boolean;
  responsive?: WindowBreakpoints & {
    isNarrow: boolean;
    isMedium: boolean;
  };
  installationProgress?: Map<string, number>;
}

export function AppStoreSection({ 
  title, 
  subtitle, 
  apps, 
  onAppAction, 
  appStore, 
  variant = 'grid',
  className,
  showEmpty = false,
  responsive,
  installationProgress
}: AppStoreSectionProps) {
  if (!apps.length && !showEmpty) return null;

  return (
    <section className={cn(
      responsive?.isNarrow ? "space-y-4" : "space-y-6", 
      className
    )}>
      <div>
        <h2 className={cn(
          "font-light text-zinc-100 mb-2 tracking-wide",
          responsive?.isNarrow ? "text-xl" : 
          responsive?.isMedium ? "text-2xl" : 
          "text-2xl"
        )}>
          {title}
        </h2>
        {subtitle && (
          <p className={cn(
            "text-zinc-400 font-light",
            responsive?.isNarrow ? "text-sm" : "text-base"
          )}>
            {subtitle}
          </p>
        )}
      </div>
      
      {apps.length === 0 ? (
        <div className={cn(
          "text-center",
          responsive?.isNarrow ? "py-8" : "py-16"
        )}>
          <div className={cn(
            "rounded-full bg-muted/20 flex items-center justify-center mx-auto mb-4",
            responsive?.isNarrow ? "w-12 h-12" : "w-16 h-16"
          )}>
            <span className={responsive?.isNarrow ? "text-xl" : "text-2xl"}>📱</span>
          </div>
          <p className={cn(
            "text-muted-foreground",
            responsive?.isNarrow ? "text-base" : "text-lg"
          )}>
            No apps found
          </p>
          <p className={cn(
            "text-muted-foreground mt-1",
            responsive?.isNarrow ? "text-xs" : "text-sm"
          )}>
            Try adjusting your search or browse other categories
          </p>
        </div>
      ) : variant === 'grid' ? (
        <ResponsiveGrid 
          minItemWidth={responsive?.isNarrow ? 200 : 240}
          maxColumns={responsive?.isNarrow ? 2 : responsive?.isMedium ? 3 : 4}
          gap={responsive?.isNarrow ? 'sm' : 'md'}
        >
          {apps.map((app) => (
            <AppStoreAppCard
              key={app.id}
              app={app}
              onAppAction={onAppAction}
              appStore={appStore}
              variant={variant}
              responsive={responsive}
              installationProgress={installationProgress}
            />
          ))}
        </ResponsiveGrid>
      ) : (
        <div className={responsive?.isNarrow ? "space-y-2" : "space-y-4"}>
          {apps.map((app) => (
            <AppStoreAppCard
              key={app.id}
              app={app}
              onAppAction={onAppAction}
              appStore={appStore}
              variant={variant}
              responsive={responsive}
              installationProgress={installationProgress}
            />
          ))}
        </div>
      )}
    </section>
  );
}